import { userStore } from "../../store/user";
import { organizationStore } from "../../store/organization";
import { eventStore, useEventStore } from "../../store/event";
import { usePermissions } from "../auth/api";
import { GET_ORDERS } from "./queries";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { usdFormat } from "../../utils";
import { TransactionReportRow } from "../../types/order";
import {Link, useLoaderData, useLocation} from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import OrderFilter from "./components/OrderFilter";
import { Badge } from "../../components/Badge";
import moment from "moment/moment";
import { parseInt } from "lodash";
import {
  ColDef,
  GridApi,
  SizeColumnsToContentStrategy,
} from "ag-grid-community";
import { Breadcrumbs } from "../../components/Breadcrumbs";
import { GET_LOCATIONS_REPORT_FILTER } from "../locations/queries";
import { AgGridReact } from "ag-grid-react";
import { SecondaryButton } from "../../components/Button";
import { buildReportCsvHeader } from "../reports/utils/buildReportCsvHeader";
import { fetchReceiptPhonesByReference } from "../reports/utils/api";
import Loader from "../../components/Loader";
import { buildPaymentTypeOr, displayPaymentType } from "./paymentTypes";

const client = createApolloClient();
const { getState } = userStore;
const { getState: getOrganizationState } = organizationStore;
const { getState: getEventState } = eventStore;

type CellRenderer = (params: {
  value:
    | string
    | number
    | boolean
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | React.ReactFragment
    | React.ReactPortal
    | null
    | undefined;
  data: TransactionReportRow;
}) => JSX.Element;

const createCellRenderer = (
  permission: boolean,
  linkFunc: (params: { data: TransactionReportRow }) => string
): CellRenderer => {
  return (params) => (
    <>
      {permission ? (
        <Link to={linkFunc(params)}>
          <Badge>{params.value}</Badge>
        </Link>
      ) : (
        <Badge>{params.value}</Badge>
      )}
    </>
  );
};



export const OrdersList = () => {
  const { ListPermission } = usePermissions("orders");
  const { editCreatePermission } = usePermissions("attendees");
  const [rowData, setRowData] = useState<TransactionReportRow[]>([]);
  const [receiptPhones, setReceiptPhones] = useState<Map<string, string>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProcessors, setSelectedProcessors] = useState<string[] | null>(null);
  const [locations, setLocations] = useState<any[]>([]);
  const location = useLocation();
  const gridRef = useRef<GridApi>();
  const orders = useLoaderData() as TransactionReportRow[];
  const allEvents = useEventStore((state) => state.allEvents);
  const currentEventId = getEventState().eventId;

  // Fetch locations with processor configs for enrichment
  useEffect(() => {
    const fetchLocations = async () => {
      const rawOrganizationId = getOrganizationState().organizationId;
      const organizationId = parseInt(rawOrganizationId !== null ? rawOrganizationId.toString() : '', 10);
      const { eventId } = getEventState();
      const user = getState().user;
      let locationWhere: any = (organizationId && organizationId !== 0)
        ? { event: { organization_id: { _eq: organizationId } } }
        : {};
      if (eventId) {
        locationWhere = { ...locationWhere, event_id: { _eq: eventId } };
      }
      if (user?.vendors) {
        locationWhere = { vendor_id: { _in: user.vendors } };
      }
      if (user?.events) {
        locationWhere = { event_id: { _in: user.events } };
      }
      try {
        const { data } = await client.query({
          query: GET_LOCATIONS_REPORT_FILTER,
          variables: { locationWhere },
        });
        setLocations(data?.locations || []);
      } catch (e) {
        console.error("Failed to fetch locations for processor mapping", e);
      }
    };
    fetchLocations();
  }, []);

  // Enrich rows with payment processor name and expected deposit
  const enrichedData = useMemo(() => {
    const locationProcessorMap = new Map<number, { name: string; processingRate: number; feePerTransaction: number }>();
    for (const loc of locations) {
      if (loc.payment_processor_config?.payment_processor) {
        locationProcessorMap.set(Number(loc.id), {
          name: loc.payment_processor_config.payment_processor,
          processingRate: loc.payment_processor_config.processing_rate ?? 0,
          feePerTransaction: loc.payment_processor_config.config?.application_fee_per_transaction ?? 0,
        });
      }
    }
    const currentEvent = allEvents?.find((e: any) => e.id === currentEventId);
    const eventProcessorName = currentEvent?.payment_processor_config?.payment_processor ?? '';
    const eventProcessingRate = currentEvent?.payment_processor_config?.processing_rate ?? 0;
    const eventFeePerTransaction = currentEvent?.payment_processor_config?.config?.application_fee_per_transaction ?? 0;

    return (rowData || []).map((row) => {
      const locProcessor = locationProcessorMap.get(row.location_id as number);
      const processingRate = locProcessor?.processingRate ?? eventProcessingRate;
      const feePerTransaction = locProcessor?.feePerTransaction ?? eventFeePerTransaction;
      const paymentType = String(row.payment_type).toLowerCase();
      const isProcessorPayment = paymentType === 'credit' || paymentType === 'rfid';
      const referenceId = row.order_payment?.reference_id;
      const receiptPhone = referenceId ? receiptPhones.get(String(referenceId)) : undefined;
      return {
        ...row,
        // "qr" for an rfid row whose legs were read by QR code; the raw
        // payment_type stays on the row for the deposit math above.
        payment_type_display: displayPaymentType(row.payment_type, row.order_payment?.cashless_charges),
        receipt_phone_number: receiptPhone,
        payment_processor_name: locProcessor?.name || eventProcessorName || undefined,
        processing_rate: processingRate || undefined,
        fee_per_transaction: feePerTransaction || undefined,
        expected_deposit: isProcessorPayment
          ? Math.round(row.transaction_amount * (1 - processingRate))
          : undefined,
      };
    });
  }, [rowData, receiptPhones, locations, allEvents, currentEventId]);

  // Derive processor filter options
  const paymentProcessorOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of enrichedData) {
      if (r.payment_processor_name) set.add(r.payment_processor_name);
    }
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
      .map((name) => ({ label: name, value: name }));
  }, [enrichedData]);

  // Apply processor filter
  const filteredData = useMemo(() => {
    if (!selectedProcessors || selectedProcessors.length === 0) return enrichedData;
    return enrichedData.filter(
      (row) => row.payment_processor_name && selectedProcessors.includes(row.payment_processor_name)
    );
  }, [enrichedData, selectedProcessors]);

  const handlePaymentProcessorChange = useCallback((value: string[] | null) => {
    setSelectedProcessors(value);
  }, []);

  const handleExportCSV = () => {
    if (gridRef.current) {
      const timestamp = new Date().toISOString().replace(/[-:]/g, "");
      const params = {
        fileName: `OrdersList_${timestamp}.csv`,
        columnSeparator: ",",
        skipHeader: false,
        allColumns: true,
        prependContent: buildReportCsvHeader({
          reportName: "Orders List",
          searchString: location.search,
        }),
      };
      gridRef.current.exportDataAsCsv(params);
    }
  };
  useEffect(() => {
    let isMounted = true;
  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      const rawOrganizationId = getOrganizationState().organizationId;
      const organizationId = parseInt(rawOrganizationId !== null ? rawOrganizationId.toString() : '', 10);
      const { eventId } = getEventState();
      const user = getState().user;
      const permissions = getState().permissions;
      const search = new URLSearchParams(location.search);
      const where = search.get("where");
      const vendorId = search.get("vendor") ?? "";
      const username = search.get("username") ?? "";
      const locationId = search.get("location") ?? null;
      const last4ofCC = search.get("last4ofCC") ?? "";
      const cardTypeParams = search.getAll("cardType");
      if (cardTypeParams.length === 0) {
        const single = search.get("cardType" ?? "");
        if (single) cardTypeParams.push(single);
      }
      const transactionAmount = search.get("transactionAmount") ?? "";
      const paymentRefId = search.get("paymentRefId") ?? "";
      const email = search.get("email") ?? "";
      const orderId = search.get("orderId") ?? "";
      const attendeeId = search.get("attendeeId") ?? "";
      const phoneNumber = search.get("phoneNumber") ?? "";
      const mxRefId = search.get("mxRefId") ?? "";
      const cashlessAssetId = search.get("cashlessAssetId") ?? "";
      const excludedVendorIds = search
        .getAll("excludeVendor")
        .map((id) => parseInt(id, 10))
        .filter((id) => Number.isFinite(id));
      const paymentTypeParams = search.getAll("paymentType");

      let whereVariable: any = where ? JSON.parse(where) : {};
      if (permissions !== "admin") {
        whereVariable = {
          ...whereVariable,
          payment_status: {
            _nin: ['pending', 'pending1'],
          },
        };
      }
      if (organizationId && organizationId !== 0) {
        whereVariable = {
          ...whereVariable,
          organization_id: {
            _eq: organizationId,
          },
        };
      }

      if (eventId) {
        whereVariable = {
          ...whereVariable,
          event_id: {
            _eq: eventId,
          },
        };
      } else if (user?.events) {
        whereVariable = {
          ...whereVariable,
          event_id: {
            _in: user?.events,
          },
        };
      }

      if (locationId !== null && locationId !== "") {
        whereVariable = {
          ...whereVariable,
          location_id: {
            _eq: parseInt(locationId, 10),
          },
        };
      }

      if (vendorId !== null && vendorId !== "") {
        whereVariable = {
          ...whereVariable,
          vendor_id: {
            _eq: parseInt(vendorId, 10),
          },
        };
      } else if (user?.vendors) {
        whereVariable = {
          ...whereVariable,
          vendor_id: {
            _in: user?.vendors,
          },
        };
      }

      // Exclusion is additive, not a replacement: keep whatever _in is already
      // there (an explicit Vendor pick, or the user's own vendor scope) and add
      // _nin on top. Hasura reads the two together as AND. Mirrors the
      // includeVendorExclusions behaviour on the Product Mix report.
      if (excludedVendorIds.length > 0) {
        whereVariable = {
          ...whereVariable,
          vendor_id: {
            ...(whereVariable.vendor_id ?? {}),
            _nin: excludedVendorIds,
          },
        };
      }
      if (username !== null && username !== "") {
        whereVariable = {
          ...whereVariable,
          username: {
            _ilike: username,
          },
        };
      }
      if (last4ofCC !== null && last4ofCC !== "") {
        whereVariable = {
          ...whereVariable,
          last4: {
            _eq: last4ofCC,
          },
        };
      }

      // Card Type and Tender each expand to a set of alternate spellings, so
      // each becomes its own _or group. They go under _and rather than into one
      // shared top-level _or — merging them would match rows satisfying EITHER
      // filter instead of both, and would also swallow any _or the caller
      // passed in via the `where` param.
      const andGroups: any[] = Array.isArray(whereVariable._and)
          ? [...whereVariable._and]
          : whereVariable._and
              ? [whereVariable._and]
              : [];

      const cardTypeOrs = cardTypeParams
          .map((v) => (v ?? "").trim())
          .filter((v) => v.length > 0)
          .map((v) => ({
            card_type: { _ilike: v.includes("%") ? v : `%${v}%` },
          }));
      if (cardTypeOrs.length > 0) {
        andGroups.push({ _or: cardTypeOrs });
      }

      const paymentTypeOrs = buildPaymentTypeOr(paymentTypeParams);
      if (paymentTypeOrs.length > 0) {
        andGroups.push({ _or: paymentTypeOrs });
      }

      if (andGroups.length > 0) {
        whereVariable = { ...whereVariable, _and: andGroups };
      }
      if (transactionAmount !== null && transactionAmount !== "") {
        whereVariable = {
          ...whereVariable,
          transaction_amount: {
            _eq: parseInt(transactionAmount, 10),
          },
        };
      }
      if (paymentRefId !== null && paymentRefId !== "") {
        whereVariable = {
          ...whereVariable,
          payment_ref_id: {
            _ilike: "%" + paymentRefId + "%",
          },
        };
      }
      if (mxRefId !== null && mxRefId !== "") {
        whereVariable = {
          ...whereVariable,
          order_mx_ref_id: {
            _ilike: "%" + mxRefId + "%",
          },
        };
      }
      if (email !== null && email !== "") {
        whereVariable = {
          ...whereVariable,
          email: {
            _ilike: "%" + email + "%",
          },
        };
      }
      if (phoneNumber !== null && phoneNumber !== "") {
        whereVariable = {
          ...whereVariable,
          order_payment: {
            phone_number: {
              _ilike: "%" + phoneNumber + "%",
            },
          },
        };
      }
      if (attendeeId !== null && attendeeId !== "") {
        whereVariable = {
          ...whereVariable,
          attendee_id: {
            _eq:  parseInt(attendeeId),
          },
        };
      }
      if (orderId !== null && orderId !== "") {
        whereVariable = {
          ...whereVariable,
          order_id: {
            _eq:  parseInt(orderId),
          },
        };
      }
      if (cashlessAssetId !== null && cashlessAssetId !== "") {
        whereVariable = {
          ...whereVariable,
          uid: {
            _ilike: "%" + cashlessAssetId + "%",
          },
        };
      } else {
        whereVariable = {
          ...whereVariable,
        };
      }
      const { data } = await client.query({
        query: GET_ORDERS,
        variables: { where: whereVariable },
      });
      const rows = data.reports_transactions || [];
      // Backfill phone numbers from receipt_track (the number the customer
      // typed for their receipt) keyed by order_payment.reference_id. NOTE: the
      // phone search box above still filters server-side on
      // order_payment.phone_number only, so a search for a number that lives
      // solely in receipt_track will not match here (display-only stitch).
      const phoneMap = await fetchReceiptPhonesByReference(
        client,
        rows.map((row: TransactionReportRow) => row.order_payment?.reference_id),
        organizationId
      );
      if (isMounted) {
        setReceiptPhones(phoneMap);
        setRowData(rows);
      }
    } catch (e) {
      console.error("Failed to load orders", e);
    } finally {
      if (isMounted) setIsLoading(false);
    }
  };

  fetchOrders();

  return () => {
    isMounted = false;
  };
}, [location.search]);

  useEffect(() => {
    setRowData(orders);
  }, [orders]);



  const generateColDefs = (): ColDef<TransactionReportRow>[] => [
    {
      headerName: "Date",
      valueGetter: (p) => p.data?.transaction_time,
      pinned: "left",
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("ddd, MMM DD, YYYY h:mm a") : "";
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (p) =>
        p.data?.transaction_time
          ? moment(p.data.transaction_time).format("ddd, MMM DD, YYYY h:mm a")
          : "",
    },
    {
      headerName: "Date_Raw",
      valueGetter: (p) => p.data?.transaction_time,
      hide: true,
    },
    {
      headerName: "Username",
      valueGetter: (p) => p.data?.username,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Order ID",
      valueGetter: (p) => p.data?.order_id,
      cellRenderer: createCellRenderer(
        ListPermission,
        (p) => `/orders/${p.data.order_payment?.reference_id}/${p.data?.order_id}`
      ),
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Ronin Ref ID",
      valueGetter: (p) => p.data?.payment_ref_id,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Payment Processor Ref ID",
      valueGetter: (p) => p.data?.order_mx_ref_id,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Order Status",
      valueGetter: (p) => p.data?.order_status,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Payment Status",
      valueGetter: (p) => p.data?.payment_status,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Payment Type",
      valueGetter: (p) => p.data?.payment_type_display ?? p.data?.payment_type,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Payment Processor",
      valueGetter: (p) => p.data?.payment_processor_name,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Volume Percentage",
      valueGetter: (p) => p.data?.processing_rate,
      valueFormatter: (params) => params.value != null ? `${(params.value * 100).toFixed(2)}%` : '',
      filter: "agTextColumnFilter",
      filterValueGetter: (p) =>
        p.data?.processing_rate != null ? `${(p.data.processing_rate * 100).toFixed(2)}%` : '',
    },
    {
      headerName: "Fee per Transaction",
      valueGetter: (p) => p.data?.fee_per_transaction,
      valueFormatter: (params) => params.value != null ? usdFormat.format(params.value) : '',
      filter: "agTextColumnFilter",
      filterValueGetter: (p) =>
        p.data?.fee_per_transaction != null ? usdFormat.format(p.data.fee_per_transaction) : '',
    },
    {
      headerName: "Cashless Asset ID",
      valueGetter: (p) => p.data?.uid,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Customer Email",
      valueGetter: (p) => p.data?.email,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Phone Number",
      valueGetter: (p) => {
        // Prefer the number the customer typed for their receipt (receipt_track),
        // falling back to the order's own phone number when none was stitched in.
        const receiptPhone = p.data?.receipt_phone_number;
        if (receiptPhone) return receiptPhone;
        const phone = p.data?.order_payment?.phone_number;
        return phone && phone.trim().toUpperCase() !== "N/A" ? phone : "";
      },
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Attendee ID",
      valueGetter: (p) => p.data?.attendee_id,
      cellRenderer: createCellRenderer(
          editCreatePermission,
          (p) => `/attendees/${p.data.attendee_id}/edit`
      ),
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Last 4 of CC",
      valueGetter: (p) => p.data?.last4,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Card Type",
      valueGetter: (p) => p.data?.card_type,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Transaction Amount",
      valueGetter: (p) => p.data?.transaction_amount,
      valueFormatter: (params) => usdFormat.format((params.value ?? 0) / 100),
      filter: "agTextColumnFilter",
      filterValueGetter: (p) => usdFormat.format((p.data?.transaction_amount ?? 0) / 100),
    },
    {
      headerName: "Item Sub Total",
      valueGetter: (p) => p.data?.item_subtotal,
      valueFormatter: (params) => usdFormat.format((params.value ?? 0) / 100),
      filter: "agTextColumnFilter",
      filterValueGetter: (p) => usdFormat.format((p.data?.item_subtotal ?? 0) / 100),
    },
    {
      headerName: "Tax",
      valueGetter: (p) => p.data?.tax,
      valueFormatter: (params) => usdFormat.format((params.value ?? 0) / 100),
      filter: "agTextColumnFilter",
      filterValueGetter: (p) => usdFormat.format((p.data?.tax ?? 0) / 100),
    },
    {
      headerName: "Tip",
      valueGetter: (p) => p.data?.tip,
      valueFormatter: (params) => usdFormat.format((params.value ?? 0) / 100),
      filter: "agTextColumnFilter",
      filterValueGetter: (p) => usdFormat.format((p.data?.tip ?? 0) / 100),
    },
    {
      headerName: "Service Fee",
      valueGetter: (p) => p.data?.digital_surcharge,
      valueFormatter: (params) => usdFormat.format((params.value ?? 0) / 100),
      filter: "agTextColumnFilter",
      filterValueGetter: (p) => usdFormat.format((p.data?.digital_surcharge ?? 0) / 100),
    },
    {
      headerName: "Expected Deposit",
      valueGetter: (p) => p.data?.expected_deposit,
      valueFormatter: (params) => params.value != null ? usdFormat.format(params.value / 100) : '',
      filter: "agTextColumnFilter",
      filterValueGetter: (p) =>
        p.data?.expected_deposit != null ? usdFormat.format(p.data.expected_deposit / 100) : '',
    },
    {
      headerName: "Applied Discount Name",
      valueGetter: (p) => p.data?.applied_discount_name,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Applied Discount Amount",
      valueGetter: (p) => p.data?.applied_discount_amount,
      valueFormatter: (params) => usdFormat.format((params.value ?? 0) / 100),
      filter: "agTextColumnFilter",
      filterValueGetter: (p) => usdFormat.format((p.data?.applied_discount_amount ?? 0) / 100),
    },
    {
      headerName: 'Applied Promo Code UID',
      valueGetter: p => p.data?.promo_code_uid,
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'Applied Promo Code Amount',
      valueGetter: p => p.data?.total_promo_discount,
      valueFormatter: params => usdFormat.format((params.value ?? 0)/100),
      filter: 'agTextColumnFilter',
      filterValueGetter: (p) => usdFormat.format((p.data?.total_promo_discount ?? 0) / 100),
    },
    {
      headerName: "Organization Name",
      valueGetter: (p) => p.data?.organization_name,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Event Name",
      valueGetter: (p) => p.data?.event_name,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Vendor Name",
      valueGetter: (p) => p.data?.vendor_name,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Location Name",
      valueGetter: (p) => p.data?.location_name,
      filter: "agTextColumnFilter",
    },
    {
      headerName: 'Is Tab?',
      valueGetter: p => p.data?.is_tab,
      valueFormatter: (params) => {
        return params.value ? "Yes" : "No";
      },
      filter: 'agTextColumnFilter',
      filterValueGetter: (p) => (p.data?.is_tab ? "Yes" : "No"),
    },
    {
      headerName: 'Tab Name',
      valueGetter: p => p.data?.tab_name,
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'Tab Nickname',
      valueGetter: p => p.data?.tab_nickname,
      filter: 'agTextColumnFilter'
    },
    {
      headerName: "Order Items Count",
      valueGetter: (p) => p.data?.order_items_count,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Device Identifier",
      valueGetter: (p) => p.data?.device_app_id,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Build ID",
      valueGetter: (p) => p.data?.build_id,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Order Notes",
      valueGetter: (p) => p.data?.order_notes,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Server Created Time",
      valueGetter: (p) => p.data?.created_at,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (p) =>
        p.data?.created_at ? moment(p.data.created_at).format("MM/DD/YYYY h:mm:ss a") : "",
    },
    {
      headerName: "Order Item Details",
      valueGetter: (p) => JSON.stringify(p.data?.items_json),
      filter: "agTextColumnFilter",
    },
  ];
  const gridOptions = {
    enableCellTextSelection: true,
  };
  const colDefs: ColDef<TransactionReportRow>[] = generateColDefs();
  const rowHeight = 50;
  const autoSizeStrategy: SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };
  return (
    <div style={{ height: "75vh" }}>
      <div
        className={"ag-theme-quartz"}
        style={{ width: "100%", height: "100%" }}
      >
        <div className="mt-2 grid gap-2 grid-cols-6">
          <Breadcrumbs />
        </div>
        <div className="bg-white px-4 pb-3 mt-2 mb-2 pt-1 rounded-md">
          <OrderFilter
            paymentProcessorOptions={paymentProcessorOptions}
            selectedPaymentProcessors={selectedProcessors}
            onPaymentProcessorChange={handlePaymentProcessorChange}
            onExportCSV={handleExportCSV}
          />
        </div>
        {isLoading ? (
            <Loader />
        ) : (
        <AgGridReact
          rowData={filteredData}
          columnDefs={colDefs}
          rowHeight={rowHeight}
          autoSizeStrategy={autoSizeStrategy}
          pagination={true}
          gridOptions={gridOptions}
          paginationPageSize={100}
          onGridReady={(params) => (gridRef.current = params.api)}
        />)}
      </div>
    </div>
  );
};
